import TransformMesh from '@modules/three/common/transformMesh/TransformMesh.ts';
import { BufferGeometry, Mesh, Quaternion, Vector3 } from 'three';
import { toothMaterial } from '@modules/three/common/materials/jawMaterials.ts';
import type { ToothSegmentationResponse } from '@/schema/toothSegmentation';
import RaycastEvents from '@modules/three/common/raycastEvents.ts';
import { capToothToGum, type ToothCappingMetadata } from '@export/aligner/toothCapping.ts';

export default class ToothExport extends TransformMesh implements RaycastEvents {
  private readonly fdi: number;
  private readonly centralAxis: Vector3;
  private cappedGeometry: BufferGeometry | null = null;
  private cappingMetadata: ToothCappingMetadata | null = null;

  constructor(geometry: BufferGeometry, data: ToothSegmentationResponse) {
    super(geometry);
    this.material = toothMaterial;
    this.fdi = data.fdi;
    this.centralAxis = new Vector3().fromArray(data.cameraPosition);
    this.setRaycastEnabled(false);

    this.centerOrigin();
    this.setPivotTransform(this.position.clone(), this.centralAxis.clone());

    this.rootState.scene.updateMatrixWorld(true);
    console.log('ToothExport', this.fdi);
  }

  public getFDI() {
    return this.fdi;
  }

  public raycastHit() {}

  public applyCorrection(movement: number[], rotation: number[]) {
    console.log('applyCorrection', this.fdi, movement, rotation);
    const [mx = 0, my = 0, mz = 0] = movement ?? [0, 0, 0];
    const [qx = 0, qy = 0, qz = 0, qw = 1] = rotation ?? [0, 0, 0, 1];

    const offset = new Vector3(mx, my, mz);
    const deltaRotation = new Quaternion(qx, qy, qz, qw).normalize();

    const targetPosition = this.getInitialPosition().clone().add(offset);
    this.setPosition(targetPosition);

    const targetRotation = this.getInitialQuaternion().clone().multiply(deltaRotation).normalize();
    this.setRotationFromQuaternion(targetRotation);
    this.rotateHalfEdges(targetRotation, targetPosition);
    this.geometry.attributes.position.needsUpdate = true;
  }

  public resetTransforms() {
    this.resetPosition();
    this.resetRotation();
  }

  /**
   * Build and cache a watertight capped version of this tooth's geometry,
   * anchored to the gum surface. Runs at JawsExport construction time so the
   * cap is computed in initial tooth-local space; subsequent rigid corrections
   * move the cap along with the tooth.
   */
  public buildCap(gumMesh: Mesh, options?: { quiet?: boolean }): void {
    if (this.cappedGeometry) {
      this.cappedGeometry.dispose();
      this.cappedGeometry = null;
    }
    this.cappingMetadata = null;
    try {
      const { geometry, metadata } = capToothToGum(this, gumMesh, options);
      this.cappedGeometry = geometry;
      this.cappingMetadata = metadata;
    } catch (error) {
      console.warn('[ToothExport] capping failed', this.fdi, error);
      this.cappedGeometry = null;
      this.cappingMetadata = null;
    }
  }

  public getCappedGeometry(): BufferGeometry | null {
    return this.cappedGeometry;
  }

  public getCappingMetadata(): ToothCappingMetadata | null {
    return this.cappingMetadata;
  }

  public dispose() {
    this.detach();
    this.removeFromParent();
    this.geometry.dispose();
    if (this.cappedGeometry) {
      this.cappedGeometry.dispose();
      this.cappedGeometry = null;
    }
  }
}

